/**
 * 贪吃猫狗：规则核心（B，pet_arcade/core；合同 docs/coordination/PET-ARCADE-SNAKE-RULES-B-2026-10-01.md）。
 *
 * 纯 TypeScript：不碰 DOM、Canvas、React、计时器、Math.random。UI 每帧 `advance(帧毫秒)`，拿回状态快照与这一帧的事件。
 * 固定步进（loop.ts）＋种子随机（rng.ts）：同一局、同一输入序列，不论帧率，结果逐字相同。
 * 局内体型只是游戏表现，不写宠物真实身体；成绩只留在这个对象里。
 */

import { createFixedStepClock, STEP_MS } from "./loop";
import { createEventStamper, type Stamped } from "./events";
import { createRng, type Rng } from "./rng";

export type ArcadeInput = { kind: "idle" } | { kind: "target"; x: number; y: number } | { kind: "stick"; dx: number; dy: number };

export interface Rect { id: string; x: number; y: number; w: number; h: number }
export interface Gate extends Rect { width: number } // 窄口：开口本身的矩形；宠物直径 ≥ width 就过不去
export interface YarnPath { id: string; ax: number; ay: number; bx: number; by: number; speed: number; radius: number }
export interface ArenaSpec { width: number; height: number; walls: Rect[]; gates: Gate[]; crates: Rect[]; yarns: YarnPath[]; spawn: { x: number; y: number } }

export interface FoodSpec { kind: FoodKind; x: number; y: number }
export interface SnakeConfig {
  durationMs: number; hearts: number; seed: number; foods: number; arena: ArenaSpec;
  /** 开局食物摆在哪（给关卡设计与用例）；不给就按种子随机刷 `foods` 个。 */
  initialFoods?: readonly FoodSpec[];
  /** 吃掉后补不补（默认补，场上保持数量）。 */
  respawn?: boolean;
}

export type FoodKind = "treat" | "leaf";
export interface Food { id: string; kind: FoodKind; x: number; y: number }
export interface Yarn { id: string; x: number; y: number; radius: number }
export type Phase = "ready" | "running" | "paused" | "ended";
export type EndReason = "time_up" | "out_of_hearts" | "quit";

export interface SnakeResult {
  reason: EndReason; counted: boolean; score: number; durationMs: number;
  eaten: number; cratesBroken: number; blocked: number; hits: number; heartsLeft: number; maxSize: number;
}

export interface SnakeState {
  phase: Phase; seed: number; elapsedMs: number; remainingMs: number; hearts: number; score: number;
  pet: { x: number; y: number; size: number; radius: number; facing: number; invulnerableMs: number };
  foods: readonly Food[]; crates: readonly Rect[]; yarns: readonly Yarn[]; walls: readonly Rect[]; gates: readonly Gate[];
  result: SnakeResult | null;
}

export type SnakeEventBody =
  | { type: "started"; seed: number; durationMs: number; hearts: number; size: number }
  | { type: "ate"; foodId: string; food: FoodKind; sizeFrom: number; sizeTo: number; points: number }
  | { type: "size_changed"; from: number; to: number; cause: FoodKind }
  | { type: "blocked"; by: "gate" | "crate"; id: string; reason: "too_big" | "too_small" }
  | { type: "crate_broken"; crateId: string; points: number }
  | { type: "hit"; yarnId: string; heartsLeft: number }
  | { type: "near_miss"; yarnId: string }
  | { type: "paused"; elapsedMs: number }
  | { type: "resumed"; elapsedMs: number }
  | { type: "ended"; reason: EndReason; counted: boolean; result: SnakeResult };

export const MIN_SIZE = 1;
export const MAX_SIZE = 5;
export const START_SIZE = 2;
export const BREAK_SIZE = 4; // 体型到 4 级才撞得开纸箱
export const RADIUS = [0, 12, 15, 19, 23, 28] as const;
export const SPEED = [0, 200, 180, 160, 140, 120] as const; // 每秒单位，越大越慢
export const FOOD_RADIUS = 9;
export const POINTS = { treat: 10, leaf: 6, crate: 25, heartLeft: 20 } as const;
export const INVULNERABLE_MS = 1200;
export const NEAR_MISS_MARGIN = 10;
const TREAT_CHANCE = 0.7;
const SPAWN_CLEARANCE = 60; // 新食物离宠物至少这么远

export const DEFAULT_ARENA: ArenaSpec = {
  width: 360,
  height: 640,
  // 中间一道横墙，留两个口：左边窄口（小才过得去），右边宽口被纸箱挡着（大才撞得开）
  walls: [
    { id: "wall-mid-left", x: 0, y: 312, w: 60, h: 16 },
    { id: "wall-mid-center", x: 104, y: 312, w: 146, h: 16 },
    { id: "wall-mid-right", x: 340, y: 312, w: 20, h: 16 },
    { id: "pillar-top", x: 160, y: 150, w: 40, h: 40 },
    { id: "pillar-bottom", x: 160, y: 460, w: 40, h: 40 },
  ],
  gates: [{ id: "gate-narrow", x: 60, y: 312, w: 44, h: 16, width: 44 }],
  crates: [{ id: "crate-1", x: 250, y: 306, w: 90, h: 28 }],
  yarns: [
    { id: "yarn-top", ax: 40, ay: 90, bx: 320, by: 90, speed: 90, radius: 12 },
    { id: "yarn-bottom", ax: 320, ay: 560, bx: 40, by: 560, speed: 110, radius: 12 },
  ],
  spawn: { x: 180, y: 400 },
};

export const DEFAULT_CONFIG: SnakeConfig = { durationMs: 60_000, hearts: 3, seed: 1, foods: 5, arena: DEFAULT_ARENA };

/** 对外的规则事件：事件体＋唯一标识 (round, step, seq)（动作清单 F0 修订 R2，见 events.ts）。 */
export type SnakeEvent = Stamped<SnakeEventBody>;

export interface SnakeGame {
  readonly state: SnakeState;
  setInput(input: ArcadeInput): void;
  advance(frameMs: number): SnakeEvent[];
  start(): SnakeEvent[];
  pause(): SnakeEvent[];
  resume(): SnakeEvent[];
  quit(): SnakeEvent[];
  restart(seed?: number): SnakeEvent[];
}

function circleHitsRect(cx: number, cy: number, r: number, rect: Rect): boolean {
  const nx = Math.max(rect.x, Math.min(cx, rect.x + rect.w));
  const ny = Math.max(rect.y, Math.min(cy, rect.y + rect.h));
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy < r * r;
}

export function createSnakeGame(overrides: Partial<SnakeConfig> = {}): SnakeGame {
  const config: SnakeConfig = { ...DEFAULT_CONFIG, ...overrides, arena: overrides.arena ?? DEFAULT_CONFIG.arena };
  const clock = createFixedStepClock();
  const stamper = createEventStamper();
  let rng: Rng;
  let seed = config.seed;
  let phase: Phase;
  let steps: number;
  let hearts: number;
  let score: number;
  let pet: SnakeState["pet"];
  let foods: Food[];
  let crates: Rect[];
  let foodSerial: number;
  let input: ArcadeInput;
  let touching: Set<string>; // 此刻正顶着的窄口／纸箱：只在「开始顶住」那一步发 blocked
  let nearing: Set<string>; // 进了擦身圈、还没碰上的毛线球：离开擦身圈那一步（宠物不在无敌期）才发 near_miss
  let touched: Set<string>; // 这一趟碰上过的：离开擦身圈之前都不再算擦身
  let stats: { eaten: number; cratesBroken: number; blocked: number; hits: number; maxSize: number };
  let result: SnakeResult | null;
  let snapshot: SnakeState;

  const durationSteps = Math.round(config.durationMs / STEP_MS);

  function yarnsAt(step: number): Yarn[] {
    return config.arena.yarns.map((path) => {
      const length = Math.hypot(path.bx - path.ax, path.by - path.ay) || 1;
      const travelled = (path.speed * step * STEP_MS) / 1000;
      const phase = travelled % (2 * length);
      const t = phase <= length ? phase / length : 2 - phase / length; // 往返
      return { id: path.id, x: path.ax + (path.bx - path.ax) * t, y: path.ay + (path.by - path.ay) * t, radius: path.radius };
    });
  }

  function solidAt(x: number, y: number, r: number): { kind: "wall" | "gate" | "crate" | "edge"; id: string } | null {
    const { width, height } = config.arena;
    if (x - r < 0 || y - r < 0 || x + r > width || y + r > height) return { kind: "edge", id: "edge" };
    for (const wall of config.arena.walls) if (circleHitsRect(x, y, r, wall)) return { kind: "wall", id: wall.id };
    for (const crate of crates) if (circleHitsRect(x, y, r, crate)) return { kind: "crate", id: crate.id };
    for (const gate of config.arena.gates) if (2 * r >= gate.width && circleHitsRect(x, y, r, gate)) return { kind: "gate", id: gate.id };
    return null;
  }

  function spawnFood(): Food {
    for (let attempt = 0; attempt < 200; attempt++) {
      const x = rng.range(FOOD_RADIUS + 4, config.arena.width - FOOD_RADIUS - 4);
      const y = rng.range(FOOD_RADIUS + 4, config.arena.height - FOOD_RADIUS - 4);
      const kind: FoodKind = rng.next() < TREAT_CHANCE ? "treat" : "leaf";
      const blocked = config.arena.walls.some((w) => circleHitsRect(x, y, FOOD_RADIUS + 2, w))
        || crates.some((c) => circleHitsRect(x, y, FOOD_RADIUS + 2, c))
        || config.arena.gates.some((g) => circleHitsRect(x, y, FOOD_RADIUS + 2, g))
        || Math.hypot(x - pet.x, y - pet.y) < SPAWN_CLEARANCE
        || foods.some((f) => Math.hypot(x - f.x, y - f.y) < FOOD_RADIUS * 3);
      if (!blocked) return { id: `food-${++foodSerial}`, kind, x, y };
    }
    return { id: `food-${++foodSerial}`, kind: "treat", x: config.arena.spawn.x, y: 40 }; // 兜底：顶部通道
  }

  function build(): SnakeState {
    const elapsedMs = steps * STEP_MS;
    return {
      phase, seed, elapsedMs, remainingMs: Math.max(0, config.durationMs - elapsedMs), hearts, score,
      pet: { ...pet }, foods: foods.map((f) => ({ ...f })), crates: crates.map((c) => ({ ...c })), yarns: yarnsAt(steps),
      walls: config.arena.walls, gates: config.arena.gates, result,
    };
  }

  function reset(newSeed: number): void {
    seed = newSeed;
    rng = createRng(newSeed);
    clock.reset();
    phase = "ready";
    steps = 0;
    hearts = config.hearts;
    score = 0;
    pet = { x: config.arena.spawn.x, y: config.arena.spawn.y, size: START_SIZE, radius: RADIUS[START_SIZE], facing: 0, invulnerableMs: 0 };
    crates = config.arena.crates.map((c) => ({ ...c }));
    foods = [];
    foodSerial = 0;
    if (config.initialFoods) foods = config.initialFoods.map((f) => ({ id: `food-${++foodSerial}`, kind: f.kind, x: f.x, y: f.y }));
    else for (let i = 0; i < config.foods; i++) foods.push(spawnFood());
    input = { kind: "idle" };
    touching = new Set();
    nearing = new Set();
    touched = new Set();
    stats = { eaten: 0, cratesBroken: 0, blocked: 0, hits: 0, maxSize: START_SIZE };
    result = null;
    snapshot = build();
  }

  function finish(reason: EndReason, events: SnakeEventBody[]): void {
    const counted = reason !== "quit";
    if (reason === "time_up") score += hearts * POINTS.heartLeft;
    phase = "ended";
    result = { reason, counted, score, durationMs: steps * STEP_MS, eaten: stats.eaten, cratesBroken: stats.cratesBroken, blocked: stats.blocked,
      hits: stats.hits, heartsLeft: hearts, maxSize: stats.maxSize };
    events.push({ type: "ended", reason, counted, result });
  }

  function velocity(): { vx: number; vy: number } {
    const speed = SPEED[pet.size];
    const perStep = (speed * STEP_MS) / 1000;
    if (input.kind === "target") {
      const dx = input.x - pet.x;
      const dy = input.y - pet.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 0.5) return { vx: 0, vy: 0 };
      const move = Math.min(perStep, dist);
      return { vx: (dx / dist) * move, vy: (dy / dist) * move };
    }
    if (input.kind === "stick") {
      const len = Math.hypot(input.dx, input.dy);
      if (len < 0.05) return { vx: 0, vy: 0 };
      const power = Math.min(1, len);
      return { vx: (input.dx / len) * power * perStep, vy: (input.dy / len) * power * perStep };
    }
    return { vx: 0, vy: 0 };
  }

  function step(events: SnakeEventBody[]): void {
    steps += 1;
    if (pet.invulnerableMs > 0) pet.invulnerableMs = Math.max(0, pet.invulnerableMs - STEP_MS);
    const { vx, vy } = velocity();
    if (vx !== 0 || vy !== 0) pet.facing = Math.atan2(vy, vx);
    const touchedNow = new Set<string>();
    // 分轴移动：撞到就退回这一轴，贴着滑、不卡死
    for (const axis of ["x", "y"] as const) {
      const delta = axis === "x" ? vx : vy;
      if (delta === 0) continue;
      const nx = axis === "x" ? pet.x + delta : pet.x;
      const ny = axis === "y" ? pet.y + delta : pet.y;
      const hit = solidAt(nx, ny, pet.radius);
      if (hit === null) {
        pet.x = nx;
        pet.y = ny;
        continue;
      }
      if (hit.kind === "crate" && pet.size >= BREAK_SIZE) {
        crates = crates.filter((c) => c.id !== hit.id);
        score += POINTS.crate;
        stats.cratesBroken += 1;
        events.push({ type: "crate_broken", crateId: hit.id, points: POINTS.crate });
        if (solidAt(nx, ny, pet.radius) === null) {
          pet.x = nx;
          pet.y = ny;
        }
        continue;
      }
      if (hit.kind === "crate" || hit.kind === "gate") {
        touchedNow.add(hit.id);
        if (!touching.has(hit.id)) {
          stats.blocked += 1;
          events.push({ type: "blocked", by: hit.kind, id: hit.id, reason: hit.kind === "gate" ? "too_big" : "too_small" });
        }
      }
    }
    touching = touchedNow;
    // 吃
    for (const food of [...foods]) {
      if (Math.hypot(food.x - pet.x, food.y - pet.y) >= pet.radius + FOOD_RADIUS) continue;
      const from = pet.size;
      const wanted = Math.max(MIN_SIZE, Math.min(MAX_SIZE, from + (food.kind === "treat" ? 1 : -1)));
      const to = wanted !== from && solidAt(pet.x, pet.y, RADIUS[wanted]) === null ? wanted : from; // 变大后会卡进墙里就先不变（吃到了、照样得分）
      const points = food.kind === "treat" ? POINTS.treat : POINTS.leaf;
      score += points;
      stats.eaten += 1;
      foods = foods.filter((f) => f.id !== food.id);
      events.push({ type: "ate", foodId: food.id, food: food.kind, sizeFrom: from, sizeTo: to, points });
      if (to !== from) {
        pet.size = to;
        pet.radius = RADIUS[to];
        stats.maxSize = Math.max(stats.maxSize, to);
        events.push({ type: "size_changed", from, to, cause: food.kind });
      }
      if (config.respawn !== false) foods.push(spawnFood());
    }
    // 毛线球
    for (const yarn of yarnsAt(steps)) {
      const dist = Math.hypot(yarn.x - pet.x, yarn.y - pet.y);
      if (dist < pet.radius + yarn.radius) {
        nearing.delete(yarn.id);
        touched.add(yarn.id);
        if (pet.invulnerableMs === 0) {
          hearts -= 1;
          stats.hits += 1;
          pet.invulnerableMs = INVULNERABLE_MS;
          events.push({ type: "hit", yarnId: yarn.id, heartsLeft: hearts });
          if (hearts <= 0) {
            finish("out_of_hearts", events);
            return;
          }
        }
      } else if (dist < pet.radius + yarn.radius + NEAR_MISS_MARGIN) {
        if (!touched.has(yarn.id)) nearing.add(yarn.id);
      } else {
        if (nearing.has(yarn.id) && pet.invulnerableMs === 0) events.push({ type: "near_miss", yarnId: yarn.id }); // 过去了、全程没碰上
        nearing.delete(yarn.id);
        touched.delete(yarn.id);
      }
    }
    if (steps >= durationSteps) finish("time_up", events);
  }

  reset(seed);

  return {
    get state() {
      return snapshot;
    },
    setInput(next: ArcadeInput): void {
      input = next;
    },
    start(): SnakeEvent[] {
      if (phase !== "ready") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<SnakeEventBody>([{ type: "started", seed, durationMs: config.durationMs, hearts, size: pet.size }], steps);
    },
    advance(frameMs: number): SnakeEvent[] {
      if (phase !== "running") return [];
      const stamped: SnakeEvent[] = [];
      const n = clock.feed(frameMs);
      for (let i = 0; i < n && phase === "running"; i++) {
        const body: SnakeEventBody[] = [];
        step(body);
        stamped.push(...stamper.stamp(body, steps));
      }
      snapshot = build();
      return stamped;
    },
    pause(): SnakeEvent[] {
      if (phase !== "running") return [];
      phase = "paused";
      clock.reset(); // 暂停前攒的半步不带到恢复之后
      snapshot = build();
      return stamper.stamp<SnakeEventBody>([{ type: "paused", elapsedMs: steps * STEP_MS }], steps);
    },
    resume(): SnakeEvent[] {
      if (phase !== "paused") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<SnakeEventBody>([{ type: "resumed", elapsedMs: steps * STEP_MS }], steps);
    },
    quit(): SnakeEvent[] {
      if (phase !== "running" && phase !== "paused") return [];
      const events: SnakeEventBody[] = [];
      finish("quit", events);
      snapshot = build();
      return stamper.stamp(events, steps);
    },
    restart(newSeed?: number): SnakeEvent[] {
      reset(newSeed ?? seed);
      stamper.nextRound();
      phase = "running";
      snapshot = build();
      return stamper.stamp<SnakeEventBody>([{ type: "started", seed, durationMs: config.durationMs, hearts, size: pet.size }], steps);
    },
  };
}
